import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invitationService, supplierEmployeeService, visitationService } from '@api/services/services';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { VisitorCardStatus } from './VisitorCard';

export interface PersonRowState {
  person: InvitedPersonDto;
  pin: string;
  status: VisitorCardStatus;
  timestamp?: string;
  errorMessage?: string | ReactNode;
}

export interface SharedFormValues {
  mode: 'walk' | 'car';
  spz?: string;
  note?: string;
}

export interface UseInvitationVisitDataResult {
  title: string;
  entryEstablishmentId: string | undefined;
  rows: PersonRowState[] | undefined;
  setRows: Dispatch<SetStateAction<PersonRowState[] | undefined>>;
  initialValues: SharedFormValues | undefined;
}

/**
 * Fetches the invitation, the persons' PINs and any active visits to build the
 * initial per-person row state for the one-time visit detail screen.
 *
 * The translation function/language are intentionally NOT in the dep array —
 * the fetched data does not depend on them. The single user-visible string
 * produced here (`oneTimePinUnavailable`) is read inside the effect via the
 * latest `t` reference captured by closure; that is acceptable for an error
 * label that the user only sees once.
 */
export const useInvitationVisitData = (invitationId: string | undefined, spzKey: string): UseInvitationVisitDataResult => {
  const { t, i18n } = useTranslation();
  const [title, setTitle] = useState<string>('');
  const [entryEstablishmentId, setEntryEstablishmentId] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<PersonRowState[] | undefined>(undefined);
  const [initialValues, setInitialValues] = useState<SharedFormValues | undefined>(undefined);

  useEffect(() => {
    if (!invitationId) return;
    let cancelled = false;
    (async () => {
      const invitationRes = await invitationService.getById(invitationId);
      if (cancelled) return;
      const invitation = invitationRes.data;
      const persons = invitation.persons ?? [];

      setTitle(invitation.name ?? '');
      setEntryEstablishmentId((invitation.workplaces ?? [])[0]?.establishment?.id);

      const spz = sessionStorage.getItem(spzKey) || '';
      setInitialValues({
        mode: spz ? 'car' : 'walk',
        spz,
        note: '',
      });

      const [pinResults, activeVisitsRes] = await Promise.all([
        Promise.allSettled(
          persons.map((p) =>
            p.employeeId
              ? supplierEmployeeService.revealPin(p.employeeId, invitationId)
              : Promise.reject(new Error('missing employeeId')),
          ),
        ),
        visitationService.query({ inAreal: true, size: 500 }).catch(() => undefined),
      ]);
      if (cancelled) return;

      const activeVisitsForInvitation = (activeVisitsRes?.data.content ?? []).filter(
        (v) => v.invitation?.id === invitationId && !v.exitTime,
      );
      const activeVisitByVisitorId = new Map(
        activeVisitsForInvitation.filter((v) => v.visitor?.id).map((v) => [v.visitor!.id, v] as const),
      );

      const nextRows: PersonRowState[] = persons.map((person, idx) => {
        const pinResult = pinResults[idx];
        const hasTraining = (person.requiredTrainings?.length ?? 0) > 0;

        const activeVisit = person.employeeId ? activeVisitByVisitorId.get(person.employeeId) : undefined;
        if (activeVisit) {
          return {
            person,
            pin: pinResult.status === 'fulfilled' ? pinResult.value.data.pin ?? '' : '',
            status: 'entered',
            timestamp: activeVisit.entryTime ? new Date(activeVisit.entryTime).toLocaleTimeString(i18n.language) : undefined,
          };
        }

        if (pinResult.status === 'fulfilled') {
          return {
            person,
            pin: pinResult.value.data.pin ?? '',
            status: hasTraining ? 'training-required' : 'pending',
          };
        }
        return {
          person,
          pin: '',
          status: 'error',
          errorMessage: t('visitation.oneTimePinUnavailable'),
        };
      });

      setRows(nextRows);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId, spzKey]);

  return { title, entryEstablishmentId, rows, setRows, initialValues };
};
